
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.removePending = false;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Intercept only the drawer's Remove button before cart.js.
    this.addEventListener('click', (event) => {
      const removeButton = event.target.closest('cart-remove-button');
      if (!removeButton || !this.contains(removeButton)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.removeCartLine(removeButton);
    }, true);
  }

  async removeCartLine(removeButton) {
    if (this.removePending) return;

    const line = Number(removeButton.dataset.index);

    if (!Number.isInteger(line) || line < 1) {
      this.showRemoveError(
        'Could not identify the cart item. Please refresh and try again.'
      );
      return;
    }

    this.removePending = true;
    removeButton.setAttribute('aria-disabled', 'true');
    removeButton.style.pointerEvents = 'none';

    let removed = false;

    // Keep the existing standardized cart event contract.
    const cartItems = removeButton.closest('cart-drawer-items');
    const quantityInput = cartItems?.querySelector(
      `#Drawer-quantity-${line}`
    );
    const lineKey = quantityInput?.dataset.quantityLineKey;
    const Events = window.StandardEvents || {};
    let eventDeferred = null;

    if (lineKey && Events.CartLinesUpdateEvent) {
      try {
        eventDeferred = Events.CartLinesUpdateEvent.createPromise();

        cartItems.dispatchEvent(
          new Events.CartLinesUpdateEvent({
            action: 'remove',
            context: 'cart',
            lines: [{ id: lineKey, quantity: 0 }],
            promise: eventDeferred.promise,
          })
        );
      } catch (error) {
        eventDeferred = null;
        console.warn('Cart event could not start:', error);
      }
    }

    try {
      const base = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${base}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          line,
          quantity: 0,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: location.pathname,
        }),
      });

      const cart = await response.json();

      if (!response.ok || cart.status || cart.errors) {
        throw new Error(
          cart.description ||
          cart.message ||
          'Shopify could not remove the item.'
        );
      }

      removed = true;

      if (eventDeferred) {
        try {
          eventDeferred.resolve({
            cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
          });
        } catch (error) {
          console.warn('Cart event resolution:', error);
        }
      }

      let sections = cart.sections;

      // Fallback only when Shopify omits rendered sections.
      if (!sections?.['cart-drawer'] || !sections?.['cart-icon-bubble']) {
        const url = new URL(location.href);
        url.searchParams.set(
          'sections',
          'cart-drawer,cart-icon-bubble'
        );

        const refresh = await fetch(url.pathname + url.search, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json'
          },
        });

        if (!refresh.ok) {
          throw new Error(
            'Item removed, but cart drawer refresh failed.'
          );
        }

        sections = await refresh.json();
      }

      if (!sections?.['cart-drawer'] || !sections?.['cart-icon-bubble']) {
        throw new Error(
          'Item removed, but cart drawer sections are missing.'
        );
      }

      // Use the existing Dawn drawer renderer.
      this.renderContents({
        id: this.productId,
        sections
      });

      // Also correctly handle removing the last cart item.
      const empty = cart.item_count === 0;

      this.classList.toggle('is-empty', empty);

      this.querySelector('.drawer__inner')
        ?.classList.toggle('is-empty', empty);

      this.querySelector('cart-drawer-items')
        ?.classList.toggle('is-empty', empty);

      // Preserve normal theme subscriber updates.
      if (
        typeof publish === 'function' &&
        window.PUB_SUB_EVENTS?.cartUpdate
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: cart
        });
      }

    } catch (error) {
      console.error('Cart drawer remove failed:', error);

      if (eventDeferred && !removed) {
        eventDeferred.reject(error);
      }

      this.showRemoveError(
        removed
          ? 'Item removed, but the cart display could not update. Refresh this page to see the latest cart.'
          : error.message ||
            'Could not remove this item. Please try again.'
      );

    } finally {
      this.removePending = false;

      if (removeButton.isConnected) {
        removeButton.removeAttribute('aria-disabled');
        removeButton.style.pointerEvents = '';
      }
    }
  }

  showRemoveError(message) {
    let target = this.querySelector('#CartDrawer-CartErrors');

    if (!target) {
      target = document.createElement('p');
      target.id = 'CartDrawer-CartErrors';
      target.setAttribute('role', 'alert');
      target.style.color = '#b42318';

      this.querySelector('.drawer__inner')?.prepend(target);
    }

    if (target) target.textContent = message;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // Original Dawn open animation.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // Existing Dawn view event remains unchanged.
    this.querySelector('cart-drawer-items')
      ?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')
      .classList.contains('is-empty') &&
      this.querySelector('.drawer__inner')
        .classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')
        .addEventListener('click', this.close.bind(this));

      this.open();
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)
      .innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
